const { VITE_REVIEW_API_URL } = import.meta.env;
const cacheKey = `reviews`;
const cachedReviews = sessionStorage.getItem(cacheKey);

/**
 * Fetches all PGO reviews with session caching.
 * @returns {Promise<Array>} Array of filtered reviews or null on error.
 */
export const fetchAllPgoReviews = async () => {
    if (cachedReviews) {
        console.log("Loaded reviews from sessionStorage");
        return JSON.parse(cachedReviews);
    }

    const url = `${VITE_REVIEW_API_URL}api/pgos`;

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        const reviews = await response.json();
        const filteredReviews = reviews.map(({ id, name, score }) => ({
            id,
            name,
            averageScore: score.averageScore,
            categoryScores: score.categoryScores,
        }));

        sessionStorage.setItem(cacheKey, JSON.stringify(filteredReviews));

        return filteredReviews;
    } catch (error) {
        console.error("Error fetching reviews:", error);
        return null;
    }
};

/**
 * Fetches all PGO reviews with session caching.
 * @returns {Promise<Array>} Array of filtered reviews or null on error.
 */
export const fetchPgoReviews = async (uuid) => {
    if (!uuid) {
        console.error("Undefined Uuid");
        return;
    }

    if (cachedReviews) {
        console.log("Loaded reviews from sessionStorage");
        return JSON.parse(cachedReviews).find(review => review.id === uuid) ?? null;
    }

    const url = `${VITE_REVIEW_API_URL}api/pgos/${uuid}`;

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        const reviews = await response.json();

        const filteredReviews = reviews.map(({ id, name, score }) => ({
            id,
            name,
            averageScore: score.averageScore,
            categoryScores: score.categoryScores,
        }));

        return filteredReviews[0];
    } catch (error) {
        console.error("Error fetching reviews:", error);
        return null;
    }
};

/**
 * Fetches reviews for a specific PGO.
 * @param {string} pgoId - The PGO ID.
 * @param {string|null} after - Cursor for pagination.
 * @param {number} perPage - Number of reviews per page.
 * @param {string} sortBy - Sorting criteria (e.g., "new_first", "old_first", "number_high", "number_low").
 * @returns {Promise<Object|null>} Reviews data or null on error.
 */
export const fetchReviews = async (pgoId, after = null, perPage = 5, sortBy = "new_first") => {
    const sortingArray = {
        new_first: "order[updatedAt]=DESC",
        old_first: "order[updatedAt]=ASC",
        number_high: "order[averageScore]=DESC",
        number_low: "order[averageScore]=ASC",
    };

    const orderQuery = sortingArray[sortBy] || sortingArray["new_first"];

    let url = `${VITE_REVIEW_API_URL}api/pgos/${pgoId}/reviews?perPage=${perPage}&${orderQuery}`;
    if (after) url += `&after=${after}`;

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        const { reviews } = await response.json();

        return {
            nodes: reviews.nodes,
            pageInfo: reviews.pageInfo,
            totalCount: reviews.totalCount,
        };
    } catch (error) {
        console.error("Error fetching reviews:", error);
        return null;
    }
};

/**
 * POST function to report a review
 * @param {string} uuid - The PGO Uuid.
 * @param {string} reviewUuid - The PGO review Uuid.
 * @param {string} formData - All the filled data from the form
 * @returns {Promise<Boolean>} True or false when form is posted
 */
export const postReportReview = async (uuid, reviewUuid, formData) => {
    if (!uuid && !reviewUuid) {
        console.error("Undefined Uuids");
        return;
    }

    const url = `${VITE_REVIEW_API_URL}api/pgos/${uuid}/reviews/${reviewUuid}/complaints`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        const result = await response.json();

        if (response.ok) {
            return true;
        }
    } catch (error) {
        console.error("Error posting report:", error);
        return null;
    }
};

/**
 * GET function to receive suggestions for the VZVZ modal
 * @returns {Promise<Array>} MedMij results
 */
export const fetchMedMij = async (name, city) => {
    if (!name && !city) {
        console.log("Undefined name or city");
        return;
    }

    const url = new URL('api/medmij', VITE_REVIEW_API_URL);
    url.searchParams.set('q', name);
    url.searchParams.set('city', city);

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        return response.json();
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        return null;
    }
};

/**
 * GET function to receive suggestions for the VZVZ modal
 * @returns {Promise<Array>} Suggestions
 */
export const fetchSuggestions = async (city) => {
    if (!city) {
        console.log("Undefined city");
        return;
    }

    const url = new URL('https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest');
    url.searchParams.set('q', city);
    url.searchParams.set('fl', 'weergavenaam');
    url.searchParams.set('fq', 'type:woonplaats');
    url.searchParams.set('rows', '20');

    try {
        const response = await fetch(url);
        
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

        return response.json();
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        return null;
    }
};